import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import {
  cutdownTitle,
  type CutdownInfo,
  type CutdownLength,
  type CutdownPreviewRow,
  type CutdownSuggestion,
} from '../../../shared/cutdown';
import Eyebrow from '../Eyebrow';
import Button from '../Button';
import { TickIcon, WarnIcon } from './cutdownIcons';
import { createLabel, missCopy, reviewHeading, shortSeconds, VERDICT_LABELS } from './cutdownCopy';

interface Props {
  info: CutdownInfo;
  /** Ready cuts, longest first. */
  suggestions: CutdownSuggestion[];
  creating: boolean;
  onCancel: () => void;
  onSuggestAgain: () => void;
  onCreate: (cuts: CutdownSuggestion[]) => Promise<void>;
}

/**
 * Review the cuts (mock-up 08 §04): length cards on the left, the cut on the
 * right with removed Audio struck through. Accept or skip per length; there is
 * no editing here, the drafts are normal editable Drafts once created.
 */
export default function CutdownReview({ info, suggestions, creating, onCancel, onCreate, onSuggestAgain }: Props) {
  const [included, setIncluded] = useState<CutdownLength[]>(() => suggestions.map((s) => s.seconds));
  // The shortest cut is the one that changes most, so it opens in view.
  const [viewing, setViewing] = useState<CutdownLength>(
    () => suggestions[suggestions.length - 1].seconds,
  );
  const [stale, setStale] = useState(() => new Set(suggestions.map((s) => s.doc_version)).size !== 1);
  const [error, setError] = useState<string | null>(null);

  const inView = suggestions.find((s) => s.seconds === viewing) ?? suggestions[0];
  const chosen = suggestions.filter((s) => included.includes(s.seconds));

  async function handleCreate() {
    setError(null);
    try {
      await onCreate(chosen);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setStale(true);
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-stretch md:items-center justify-center px-0 md:px-6 py-0 md:py-8 z-50 animate-fade-in"
      onClick={creating ? undefined : onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cutdown-review-title"
        onClick={(e) => e.stopPropagation()}
        className="
          w-full md:max-w-5xl md:max-h-[90vh] overflow-hidden
          bg-card border border-line md:rounded-md
          flex flex-col animate-scale-in
        "
      >
        <header className="px-5 md:px-8 py-5 border-b border-line shrink-0">
          <Eyebrow accent>// Review cutdowns</Eyebrow>
          <h2
            id="cutdown-review-title"
            className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-1.5 break-words"
          >
            {reviewHeading(suggestions.length, info.title)}
          </h2>
          <p className="text-muted text-[14px] leading-[1.55] max-w-[70ch]">
            Struck lines come out of the Audio. Visual stays as it is. Untick a length to skip it; you can
            edit each draft once it's created.
          </p>
        </header>

        <div className="flex-1 min-h-0 flex flex-col md:grid md:grid-cols-[260px_1fr]">
          <div className="shrink-0 flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto px-4 md:pl-6 md:pr-4 py-3 md:py-5 border-b md:border-b-0 md:border-r border-line bg-page">
            {suggestions.map((s) => (
              <LengthCard
                key={s.seconds}
                suggestion={s}
                inView={s.seconds === inView.seconds}
                included={included.includes(s.seconds)}
                onView={() => setViewing(s.seconds)}
                onToggle={() =>
                  setIncluded((prev) =>
                    prev.includes(s.seconds) ? prev.filter((l) => l !== s.seconds) : [...prev, s.seconds],
                  )
                }
              />
            ))}
            <p className="hidden md:block text-[11.5px] text-faint leading-[1.5] mt-2 px-0.5">
              Runtimes use the same 140 words per minute as the editor's badge, before rounding.
            </p>
          </div>

          <CutPane key={inView.seconds} info={info} suggestion={inView} />
        </div>

        <footer className="flex items-center justify-between gap-3 px-5 md:px-8 py-4 border-t border-line bg-elevated shrink-0">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={creating}>
            Cancel
          </Button>
          <div className="flex items-center gap-2.5 min-w-0">
            {error || stale ? (
              <span role="alert" className="text-error text-[13px] leading-[1.4] text-right">{error ?? 'This script changed after the cuts were suggested. Suggest them again.'}</span>
            ) : (
              <span className="hidden sm:inline font-mono text-[11px] tracking-[0.12em] uppercase text-faint mr-1.5 whitespace-nowrap">
                {chosen.length} of {suggestions.length} selected
              </span>
            )}
            {stale ? (
              <Button type="button" variant="primary" onClick={onSuggestAgain} disabled={creating}>
                Suggest again
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                className="shrink-0"
                disabled={creating || chosen.length === 0}
                onClick={handleCreate}
              >
                {creating ? 'Creating…' : createLabel(chosen.length)}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function LengthCard({
  suggestion: s,
  inView,
  included,
  onView,
  onToggle,
}: {
  suggestion: CutdownSuggestion;
  inView: boolean;
  included: boolean;
  onView: () => void;
  onToggle: () => void;
}) {
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (inView) card.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [inView]);

  return (
    <div
      ref={card}
      role="button"
      tabIndex={0}
      aria-pressed={inView}
      aria-label={`Show the ${s.seconds} second cut`}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView();
        }
      }}
      className={`
        flex gap-3 items-start p-3 rounded-sm border bg-card cursor-pointer shrink-0 min-w-[168px] md:min-w-0
        transition-colors duration-150 focus:outline-none focus-visible:shadow-focus
        ${inView ? 'border-accent shadow-[inset_0_0_0_1px_var(--color-accent)]' : 'border-line hover:border-fg/40'}
        ${included ? '' : 'opacity-55'}
      `}
    >
      <label
        className="shrink-0 -m-3.5 mt-[-11px] w-11 h-11 inline-flex items-center justify-center cursor-pointer"
        // Ticking never swaps the pane.
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={included}
          aria-label={`Include the ${s.seconds} second draft`}
          onChange={onToggle}
          className="sr-only peer"
        />
        <span
          aria-hidden="true"
          className={`w-4 h-4 rounded-[4px] border-[1.5px] inline-flex items-center justify-center peer-focus-visible:shadow-focus ${
            included ? 'bg-accent border-accent text-accent-fg' : 'border-faint/60 bg-card'
          }`}
        >
          {included && <TickIcon />}
        </span>
      </label>
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] font-semibold text-fg">{s.seconds} seconds</span>
        <span className="flex items-baseline gap-2 mt-1">
          <b className="font-display text-[20px] font-semibold leading-[1.1] text-fg">
            {shortSeconds(s.runtime_seconds)}
          </b>
          <span className="text-[12px] text-faint">of {s.seconds}s</span>
        </span>
        <span
          className={`inline-flex items-center gap-[5px] mt-1.5 font-mono text-[9.5px] tracking-[0.12em] uppercase font-semibold ${
            s.verdict === 'on_target' ? 'text-success' : 'text-warning'
          }`}
        >
          {s.verdict === 'on_target' ? <TickIcon /> : <WarnIcon size={10} />}
          {VERDICT_LABELS[s.verdict]}
        </span>
        <span className="hidden md:block text-[11.5px] text-faint mt-1">
          {s.words} of {s.total_words} words kept
        </span>
      </span>
    </div>
  );
}

function CutPane({ info, suggestion }: { info: CutdownInfo; suggestion: CutdownSuggestion }) {
  const miss = missCopy(suggestion);
  // Section labels only help when the script has more than one (ad variants).
  const sections = new Set(suggestion.rows.map((r) => r.section).filter(Boolean));
  const showSections = sections.size > 1;

  return (
    <div className="min-w-0 min-h-0 overflow-y-auto px-4 md:pl-7 md:pr-8 py-5 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1 mb-1.5">
        <div className="min-w-0">
          <div className="font-display font-semibold text-[20px] tracking-[-0.01em] text-fg break-words">
            {cutdownTitle(info.title, suggestion.seconds, [])}
          </div>
          <div className="text-[12.5px] text-faint mt-0.5 break-words">
            New draft in {info.client_name} · {info.project_name}
          </div>
        </div>
        <div className="text-[12px] text-faint whitespace-nowrap">
          <Struck>Struck</Struck> removed from Audio
        </div>
      </div>

      {miss && (
        <div className="flex gap-2.5 mt-3 mb-1 px-3.5 py-2.5 rounded-sm bg-warning/10 border border-warning/25 text-[13px] leading-[1.5] text-muted">
          <WarnIcon className="shrink-0 mt-0.5 text-warning" />
          <span>
            <b className="text-warning font-semibold">{miss.lead}</b> {miss.body}
          </span>
        </div>
      )}

      <div className="hidden md:grid grid-cols-2 gap-2 mt-3.5 mb-1">
        <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase text-faint px-4 py-1.5 border-b border-line">
          Audio
        </span>
        <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase text-accent px-4 py-1.5 border-b border-line">
          Visual
        </span>
      </div>

      {suggestion.rows.map((row, i) => {
        const prev = suggestion.rows[i - 1];
        const label = showSections && row.section && row.section !== prev?.section ? row.section : null;
        return (
          <div key={row.number}>
            {label && (
              <div className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-faint mt-5 mb-1 px-1">
                {label}
              </div>
            )}
            <CutRow row={row} first={i === 0 || !!label} />
          </div>
        );
      })}
    </div>
  );
}

function CutRow({ row, first }: { row: CutdownPreviewRow; first: boolean }) {
  const paragraphs = row.audio.filter((p) => p.length > 0);
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-2 my-2 ${first ? '' : 'pt-2 border-t border-line-soft'}`}>
      <Cell marker="A">
        {paragraphs.map((segments, i) => (
          <p key={i} className={i > 0 ? 'mt-2' : ''}>
            {segments.map((seg, j) => (seg.removed ? <Struck key={j}>{seg.text}</Struck> : <span key={j}>{seg.text}</span>))}
          </p>
        ))}
        {row.no_audio && (
          <span className="inline-block mt-1.5 font-mono text-[9.5px] tracking-[0.12em] uppercase text-faint border border-dashed border-faint/60 rounded-full px-2 py-0.5">
            No audio in this cut
          </span>
        )}
      </Cell>
      <Cell marker="V">
        {row.visuals.map((line, i) => (
          <p key={i} className={i > 0 ? 'mt-2' : ''}>{line}</p>
        ))}
      </Cell>
    </div>
  );
}

function Cell({ marker, children }: { marker: 'A' | 'V'; children: React.ReactNode }) {
  return (
    <div
      className={`relative pl-9 pr-4 py-3.5 rounded-sm bg-elevated text-[14px] leading-[1.6] whitespace-pre-line break-words ${
        marker === 'V' ? 'text-muted' : 'text-fg'
      }`}
    >
      <span
        className={`absolute top-4 left-3.5 font-mono text-[10px] font-semibold tracking-[0.12em] ${
          marker === 'V' ? 'text-accent' : 'text-faint'
        }`}
        aria-label={marker === 'A' ? 'Audio' : 'Visual'}
      >
        {marker}
      </span>
      {children}
    </div>
  );
}

/** Removed Audio, in the version-history diff style. */
function Struck({ children }: { children: React.ReactNode }) {
  return (
    <s className="bg-error/15 border-b-2 border-error decoration-error/70 text-muted rounded-[2px] px-0.5 -mx-0.5">
      {children}
    </s>
  );
}
